'use client';

/* eslint-disable no-unused-vars */

import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

type Props = {
  siteKey: string;
  action: string;
  className?: string;
  appearance?: 'always' | 'execute' | 'interaction-only';
  resetKey?: number;
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  onInteractionChange?: (interactive: boolean) => void;
};

export default function TurnstileWidget({
  siteKey,
  action,
  className,
  appearance = 'always',
  resetKey = 0,
  onVerify,
  onExpire,
  onError,
  onInteractionChange,
}: Props) {
  const container = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const callbacks = useRef({ onVerify, onExpire, onError, onInteractionChange });
  const [scriptReady, setScriptReady] = useState(false);
  callbacks.current = { onVerify, onExpire, onError, onInteractionChange };

  useEffect(() => {
    if (!scriptReady || !siteKey || !container.current || !window.turnstile) return undefined;
    if (widgetId.current) window.turnstile.remove(widgetId.current);
    widgetId.current = window.turnstile.render(container.current, {
      sitekey: siteKey,
      action,
      theme: 'light',
      size: 'flexible',
      appearance,
      execution: 'render',
      callback: (token: string) => callbacks.current.onVerify(token),
      'before-interactive-callback': () => callbacks.current.onInteractionChange?.(true),
      'after-interactive-callback': () => callbacks.current.onInteractionChange?.(false),
      'expired-callback': () => callbacks.current.onExpire?.(),
      'error-callback': () => callbacks.current.onError?.(),
    });

    return () => {
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [action, appearance, resetKey, scriptReady, siteKey]);

  return (
    <>
      <Script
        id="cloudflare-turnstile"
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        strategy="afterInteractive"
        onReady={() => setScriptReady(true)}
      />
      <div className={className} ref={container} />
    </>
  );
}
